import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8hf39rh5tg6socjugg";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    // honeypot check before any request
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("感谢反馈，我们已收到你的意见！");
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        "";

      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && !isSpam && (parsed ? parsed.code === "OK" : true);

      if (ok) {
        setStatus("success");
        setSuccessMsg("感谢反馈，我们已收到你的意见！");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
    >
      <h3 className="text-lg font-bold text-foreground-950">意见反馈</h3>
      <p className="mt-1.5 text-sm text-foreground-500">
        你的建议是我们持续优化的动力，欢迎留下你的想法。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="便于我们回复你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-sm text-foreground-700">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="type"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        >
          <option value="功能建议">功能建议</option>
          <option value="内容纠错">内容纠错</option>
          <option value="资源需求">资源需求</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm text-foreground-700">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请描述你的建议或遇到的问题（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      </div>

      <div className="form-extra-field" aria-hidden="true">
        <label htmlFor="fb-website-alt">备用联系</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          </span>
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>

        {status === "success" && (
          <span className="flex items-center gap-2 text-sm text-primary-600">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-checkbox-circle-line" />
            </span>
            {successMsg}
          </span>
        )}
        {status === "error" && (
          <span className="flex items-center gap-2 text-sm text-secondary-700">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line" />
            </span>
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}